import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';

import { projectContextSelectors } from '@blaze-shared/app-state-data-access';
import {
  getBackground,
  getBorderColor,
  makeFontStyles,
  makeFontStyleStyles,
} from '@blaze-shared/elements-domain';
import { VarUser } from '@blaze-shared/schema-domain';

import { ElementLinkService } from '../../services/element-link.service';
import { getUserInitialsFromColumn } from '../../utils/data/value.utils';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { AvatarMenuAction, AvatarPropsModel } from './avatar.models';

@UntilDestroy()
@Component({
  selector: 'be-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatMenuModule],
  host: {
    '[style.--b-av-label-color]': 'labelColor',
    '[style.--b-av-image-size]': 'imageSize',
  },
})
export class AvatarComponent
  extends CanvasElementBaseComponent<'avatar'>
  implements OnChanges, OnInit, OnDestroy
{
  private readonly elementLinkService = inject(ElementLinkService);

  private readonly elementKeyRoot = 'b-av';

  protected readonly imageUrl = signal<string | undefined>(undefined);
  protected readonly initials = signal<string | undefined>(undefined);
  protected readonly menuEnabled = signal(false);
  protected readonly name = signal<string | undefined>(undefined);

  protected containerClasses: string[] = [];
  protected elementKey?: string;
  protected fontStyles: Record<string, string> = {};
  protected imageSize = '48px';
  protected initialsStyles: Record<string, string> = {};
  protected labelColor = '#000';

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.props) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      }
      this.updateStyles();
    }
  }

  ngOnInit(): void {
    combineLatest({
      props: this.props$,
      user: this.store.select(projectContextSelectors.selectVarUser),
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ props, user }) => {
        this.setMenu(props?.content?.menu);
        this.setName(user, props?.content.fields?.name);
        this.setImage(user, props);
      });
  }

  ngOnDestroy(): void {
    if (this.elementKey) {
      this.stylesCleanup.dispose(this.elementKey);
    }
  }

  menuActionClicked(action: AvatarMenuAction): void {
    this.elementLinkService.handleLinkTrigger(action.clickAction);
  }

  private setImage(user: VarUser | undefined, props: AvatarPropsModel | undefined): void {
    this.imageUrl.set(undefined);
    this.initials.set(undefined);

    const { avatarType, avatarTypeFallback, image, imageFallback, initials, initialsFallback } =
      props?.content.fields ?? {};

    const userImage = image ? user?.[image] : '';
    const userImageFallback = imageFallback ? user?.[imageFallback] : '';
    const userInitials = getUserInitialsFromColumn(user, initials);
    const userInitialsFallback = getUserInitialsFromColumn(user, initialsFallback);

    // Main image/initials
    if (avatarType === 'image' && userImage && typeof userImage === 'string') {
      this.imageUrl.set(userImage);
      return;
    }
    if (avatarType === 'initials' && userInitials) {
      this.initials.set(userInitials);
      return;
    }

    // Fallback to image
    if (
      avatarTypeFallback === 'image' &&
      userImageFallback &&
      typeof userImageFallback === 'string'
    ) {
      this.imageUrl.set(userImageFallback);
    }

    // Fallback to initials
    if (avatarTypeFallback === 'initials' && userInitialsFallback) {
      this.initials.set(userInitialsFallback);
    }
  }

  private setMenu(menuProps: AvatarPropsModel['content']['menu'] | undefined): void {
    this.menuEnabled.set((menuProps?.enabled ?? false) && (menuProps?.items ?? []).length > 0);
  }

  private setName(user: VarUser | undefined, name: string | undefined): void {
    const userName = name && user?.[name];
    this.name.set(typeof userName === 'string' ? userName : '');
  }

  private updateStyles(): void {
    const { style } = this.props ?? {};
    const { label, font, menu, layout, initials: initialsStyle } = style ?? {};
    const { default: defaultState, hover: hoverState } = menu ?? {};
    if (!label || !font || !menu || !layout || !defaultState || !hoverState || !initialsStyle) {
      return;
    }

    this.labelColor = label.color.color1;
    this.imageSize = layout.imageSize;
    this.containerClasses = [
      `position-${layout.imagePosition}`,
      `align-${layout.labelAlign.horizontal}`,
    ];

    this.fontStyles = {
      ...makeFontStyles(font),
      ...makeFontStyleStyles(font.style),
      ['font-weight']: this.fontsService.getFontWeight(font.fontFamily, font.fontWeight),
    };

    if (initialsStyle) {
      this.initialsStyles = {
        ...makeFontStyles(initialsStyle.font),
        ...makeFontStyleStyles(initialsStyle.font.style),
        ['font-weight']: this.fontsService.getFontWeight(
          initialsStyle.font.fontFamily,
          initialsStyle.font.fontWeight,
        ),
        background: getBackground(initialsStyle.background),
        color: initialsStyle.textColor.color1,
      };
    }

    this.stylesCleanup.update(
      `${this.elementKey}`,
      `
        .b-av-m.${this.elementKey} {
          background: ${getBackground(menu.background)};
          border-color: ${getBorderColor(menu.outline)};
          --b-av-menu-item-default-label-color: ${defaultState.label.color1};
          --b-av-menu-item-default-background: ${getBackground(defaultState.background)};
          --b-av-menu-item-default-border-color: ${getBorderColor(defaultState.outline)};
          --b-av-menu-item-hover-label-color: ${hoverState.label.color1};
          --b-av-menu-item-hover-background: ${getBackground(hoverState.background)};
          --b-av-menu-item-hover-border-color: ${getBorderColor(hoverState.outline)};
        }
      `,
    );
  }
}
